<template>
  <div class="container p-4 mx-auto">
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-custom-yellow"></div>
    </div>
    <div v-else>
    <AlertLayout title="Reminders!"
      message="Kindly note that only blood units registered on Damu-Sasa will be found by the search.<br>Non-registered units should 1st be registered."
      type="warning" class="mb-4" />
    <div class="px-5 py-4 bg-white border border-t-0 rounded-lg shadow-md">
      <div v-if="!product.original_unit_serials && product.blood_donation_id" class="p-6 mb-3 border-b rounded-lg bg-gray-10">
        <!-- Collapsible Donor Information - only show if blood_donation_id exists -->
        <div @click="showDonorInfo = !showDonorInfo" class="flex items-center justify-between cursor-pointer">
          <h3 class="text-lg font-bold">Donor Information</h3>
          <svg class="w-5 h-5 transition-transform" :class="showDonorInfo ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div v-show="showDonorInfo && product.blood_donation_id" class="grid grid-cols-2 gap-4 mt-4">
          <!-- Left Column --> 
          <div class="space-y-6">
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Donor Name:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.donor?.user?.first_name ?? '' }}
                {{ product.blood_donation?.donor?.user?.last_name ?? '' }}
              </p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Donor ID:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.donor?.user?.id_number ?? '' }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Gender:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.donor?.user?.gender ?? '' }}</p>
            </div>
          </div>
          <!-- Right Column -->
          <div class="space-y-6">
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Date of Birth:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.donor?.user?.dob ?? '' }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Phone:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.donor?.user?.phone_number ?? '' }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Email:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.donor?.user?.email ?? '' }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="p-6 mb-6 rounded-lg bg-gray-10">
        <h3 class="mb-4 text-lg font-bold">Blood Unit Information</h3>
        <div class="grid grid-cols-2 gap-4">
          <!-- Left Column --> 
          <div class="space-y-6">
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Serial Number:</label>
              <p class="mt-3 text-sm font-normal">
                {{ product.pint_serial }}
              </p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Blood Group:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_group?.description ?? 'Unknown' }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Blood Volume:</label>
              <p class="mt-3 text-sm font-normal">{{ product.volume }} ml</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Blood Status:</label>
              <p class="mt-3 text-sm font-normal">{{ product.status }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Donation Date:</label>
              <p class="mt-3 text-sm font-normal">{{ product.blood_donation?.date_added?  new Date(product.blood_donation?.date_added).toLocaleString() : 'User specified'}}</p>
            </div>
          </div>
          <!-- Right Column -->
          <div class="space-y-6">
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Blood Product:</label>
              <p class="mt-3 text-sm font-normal">{{ product.unit_product?.description }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Source Unit: </label>
              <p class="mt-3 text-sm font-normal">{{ product.source  }}</p>
            </div>
            <div v-if="product.blood_donation?.pint_serial || product.original_unit_serials" class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Original Units: </label>
              <p class="mt-3 text-sm font-normal">{{ formattedOriginalUnitSerials }}</p>
            </div>
            <div class="grid items-center grid-cols-2 m-2 border-t">
              <label class="mt-3 text-sm font-normal">Expiry Date:</label>
              <p class="mt-3 text-sm font-normal">{{ (new Date(product.expiry_date).toLocaleDateString()) ?? '' }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Centrifugation Settings -->
      <div v-if="product.centrifuge_setting_id" class="p-6 mb-6 rounded-lg bg-gray-10">
        <div @click="showCentrifugeInfo = !showCentrifugeInfo" class="flex items-center justify-between cursor-pointer">
          <h3 class="text-lg font-bold">Centrifugation Settings</h3>
          <svg class="w-5 h-5 transition-transform" :class="showCentrifugeInfo ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div v-show="showCentrifugeInfo" class="grid grid-cols-2 gap-4 mt-4">
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Centrifuge Code:</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.centrifuge_code ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Centrifuge Batch:</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.centrifuge_batch ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Speed (RPM):</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.speed ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Time (minutes):</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.centrifuge_time ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Temperature (°C):</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.temperature ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Brake:</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.brake ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Spin Type:</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.spin_type ?? '' }}</p>
          </div>
          <div class="grid items-center grid-cols-2 m-2 border-t">
            <label class="mt-3 text-sm font-normal">Performed By:</label>
            <p class="mt-3 text-sm font-normal">{{ product.centrifuge_setting?.centrifuge_performed_by_user?.first_name ?? '' }} {{ product.centrifuge_setting?.centrifuge_performed_by_user?.last_name ?? '' }}</p>
          </div>
        </div>
      </div>

      <div v-if="showWbSelectionModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
          <div class="p-6 border-b">
            <div class="flex items-center justify-between">
              <h2 class="text-xl font-semibold text-gray-500">Select Units</h2>
              <button @click="showWbSelectionModal = false" class="text-2xl text-gray-500 hover:text-gray-700">×</button>
            </div>
          </div>
          <div class="p-6">
            <div v-if="wbSelectionLoading" class="flex justify-center items-center min-h-[100px]">
              <div class="w-8 h-8 border-b-2 rounded-full animate-spin border-custom-yellow"></div>
            </div>
            <div v-else>
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Select</th>
                    <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Serial</th>
                    <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Blood Group</th>
                    <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Volume</th>
                    <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Expiry Date</th>
                  </tr>
                </thead>
<tbody class="bg-white divide-y divide-gray-200">
                   <tr v-for="unit in wholeBloodUnits" :key="unit.product_id" class="hover:bg-gray-50">
                     <td class="px-4 py-2">
                       <input 
                         type="checkbox" 
                         :value="unit.product_id" 
                         v-model="selectedWbUnitIds"
                       />
                     </td>
                     <td class="px-4 py-2 text-sm">{{ unit.pint_serial }}</td>
                     <td class="px-4 py-2 text-sm">{{ unit.blood_group?.description }}</td>
                     <td class="px-4 py-2 text-sm">{{ unit.volume }} ml</td>
                     <td class="px-4 py-2 text-sm">{{ unit.expiry_date ? new Date(unit.expiry_date).toLocaleDateString() : '-' }}</td>
                   </tr>
                 </tbody>
              </table>
              <div v-if="wholeBloodUnits.length === 0" class="py-4 text-center text-gray-500">
                No whole blood units available
              </div>
            </div>
            <div class="flex justify-end mt-4 space-x-2">
              <button @click="showWbSelectionModal = false" class="px-4 py-2 text-gray-600 border rounded">Cancel</button>
              <button @click="proceedToComponentPrep" class="px-4 py-2 text-white rounded bg-custom-yellow" :disabled="selectedWbUnitIds.length === 0">
                Proceed to Component Prep
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-between">
        <div>
<button v-if="product.status == 'Okay' && !(product.facility_issuing && facility_code === 'KTTA') && product.unit_product?.name === 'WB'" class="px-4 py-2 mr-2 font-bold text-white rounded bg-custom-yellow"
             @click="handleComponentPrep('Split')">
             Component Preparation
           </button>
           <button  v-if="product.status == 'Okay' && !(product.facility_issuing && facility_code === 'KTTA')" class="px-4 py-2 mr-2 font-bold text-white rounded bg-custom-yellow"
             @click="handleComponentPrep('Requote')">
             Aliquot
           </button>
          <!-- <button
            v-if="product.unit_product?.name !== 'LDPRC'"
            class="px-4 py-2 mr-2 font-bold text-white rounded bg-custom-yellow"
            @click="handleComponentPrep(flag = 'Leuco-reduction')">
            Leuco Reduction
          </button> -->
        </div>
        <div>
          <button class="px-4 py-2 mr-2 text-orange-500 border border-orange-500 rounded"
            @click="handleBackToUnitDetails">
            Back
          </button>
          <button v-if="product.status === 'Okay' && !(product.facility_issuing && facility_code === 'KTTA')" class="px-4 py-2 mr-2 font-bold text-white rounded bg-custom-yellow disabled:opacity-50"
            @click="handleSegregation" :disabled="isSubmitting">
            <span v-if="isSubmitting">Processing...</span>
            <span v-else>Segregate</span>
          </button>
          <button v-if="['Segregated', 'Assigned', 'Issued'].includes(product.status) || (product.status && product.status.startsWith('Issued to') && !(product.facility_issuing && facility_code === 'KTTA'))"
          class="px-4 py-2 mr-2 font-bold text-white rounded bg-custom-yellow" 
          @click="handleReturnUnit">
          Return to Inventory
        </button>
        </div>
      </div>
    </div>
    </div>

  </div>
</template>

<script>
import AlertLayout from '@/components/AlertLayout.vue';
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: 'ProductDetailsPage',
  components: {
    AlertLayout,
  },
  data() {
    return {
      donation: [],
      product: [],
      product_id: '',
      totalVolume: 0,
      error: null,
      showDeferralPopup: false,
      userData: null,
      facility_code: null,
      showDonorInfo: false,
      showCentrifugeInfo: false,
      loading: false,
      isSubmitting: false,
      showWbSelectionModal: false,
      wholeBloodUnits: [],
      selectedWbUnitIds: [],
      wbSelectionLoading: false,
      currentFlag: '',
    };
  },
  computed: {
    formattedOriginalUnitSerials() {
      const serials = this.product.original_unit_serials;
      if (!serials) return '-';
      if (Array.isArray(serials)) {
        return serials.length > 0 ? serials.join(', ') : '-';
      }
      if (typeof serials === 'string') {
        try {
          const parsed = JSON.parse(serials);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.join(', ');
          }
          return parsed || '-';
        } catch (e) {
          return serials || '-';
        }
      }
      return String(serials) || '-';
    }
  },
  created() {
    this.product_id = this.$route.params.product_id;
    this.loading = true;
    this.getBloodProduct();
  },

  mounted() {
      this.userData = this.$store.getters["auth/user"];
      this.facility_code = this.userData?.user_roles[0]?.facility_department?.facility?.facility_code || '';
  },

  methods: {
    user() {
      return this.$store.getters["auth/user"];
    },
    handleBackToUnitDetails() {
      this.$router.push({ name: 'InventoryManagementPage' });
    },    
async handleComponentPrep(flag) {
       if (!this.product_id) {
         console.error('Invalid donationId');
         return;
       }
       
       this.currentFlag = flag;
       
       // For Requote (Aliquot), skip the modal and go directly to component preparation
       if (flag === 'Requote') {
         this.$router.push({
           name: 'ComponentPreparationPage',
           params: {
             product_id: this.product_id,
             flag: this.currentFlag
           },
           query: { source_unit_ids: this.product_id }
         });
         return;
       }
       
       try {
         this.wbSelectionLoading = true;
         const response = await api.fetchWholeBloodUnits();
         this.wholeBloodUnits = response || [];
         
         // Ensure current product is in the list and pre-checked
         if (!this.wholeBloodUnits.some(u => u.product_id === this.product_id)) {
           const currentProduct = await api.fetchSingleProduct(this.product_id);
           this.wholeBloodUnits.unshift(currentProduct.data || currentProduct);
         }
         this.selectedWbUnitIds = [this.product_id];
         this.showWbSelectionModal = true;
       } catch (error) {
         console.error('Error fetching whole blood units:', error);
         SweetAlertService.showError('Error', 'Failed to fetch whole blood units.');
       } finally {
         this.wbSelectionLoading = false;
       }
     },
    
    async proceedToComponentPrep() {
      if (this.selectedWbUnitIds.length === 0) {
        SweetAlertService.showError('Error', 'Please select at least one unit.');
        return;
      }
      
      const selectedIds = this.selectedWbUnitIds.join(',');
      this.$router.push({
        name: 'ComponentPreparationPage',
        params: {
          product_id: this.product_id,
          flag: this.currentFlag
        },
        query: { source_unit_ids: selectedIds }
      });
      this.showWbSelectionModal = false;
    },
    async handleReturnUnit() {
      try {
        const result = await SweetAlertService.showConfirmation(
          'Are you sure?',
          'Do you want to return this unit to inventory?'
        );

        if (result.isConfirmed) {
          await api.returnSegregatedUnit(this.product_id);
          await SweetAlertService.showSuccess('Success!', 'Unit returned to inventory successfully!');
          this.$router.go(0);
        }
      } catch (error) {
        console.error('Error:', error.response?.data?.error || 'An error occurred.');
      }
    },

    async handleSegregation() {
      this.isSubmitting = true;
      try {
        await api.unitSegregation(this.product_id);
        await SweetAlertService.showSuccess('Success!', 'Blood unit segregated successfully!.');
        this.$router.go(0);
      } catch (error) {
        console.error('Error:', error.response.data.error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async getBloodProduct() {
      this.loading = true;
      try {
        const response = await api.fetchSingleProduct(this.product_id);
        this.product = response.data || response;
      } catch (error) {
        this.error = error.message || 'Failed to fetch donation';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>